import * as React from "react"
import { cn } from "@/lib/utils"

export interface UnitPickerProps {
  units: string[]
  selectedUnit: string
  onSelect: (unit: string) => void
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  className?: string
}

export function UnitPicker({ 
  units, 
  selectedUnit, 
  onSelect, 
  isOpen, 
  onOpenChange,
  className 
}: UnitPickerProps) {
  const pickerRef = React.useRef<HTMLDivElement>(null)

  // Close on outside click
  React.useEffect(() => {
    if (!isOpen) return
    
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        onOpenChange(false)
      }
    }
    
    // Use timeout to avoid immediate close from the click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)
    
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onOpenChange])

  // Close on escape
  React.useEffect(() => {
    if (!isOpen) return
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false)
      }
    }
    
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onOpenChange])

  if (!isOpen) return null

  return (
    <div 
      ref={pickerRef}
      className={cn(
        "absolute top-full left-1/2 -translate-x-1/2 mt-1.5",
        "bg-white rounded-xl shadow-lg border border-neutral-200",
        "py-1.5 z-50 min-w-[100px]",
        "animate-in fade-in-0 zoom-in-95 duration-100",
        className
      )}
    >
      {units.map((unit) => (
        <button
          key={unit}
          type="button"
          onClick={() => {
            onSelect(unit)
            onOpenChange(false)
          }}
          className={cn(
            "w-full px-4 py-2 text-left text-sm font-medium transition-colors",
            unit === selectedUnit 
              ? "text-neutral-900 bg-neutral-100" 
              : "text-neutral-600 hover:bg-neutral-50"
          )}
        >
          {unit}
        </button>
      ))}
    </div>
  )
}

UnitPicker.displayName = "UnitPicker"
